import React, { memo } from 'react';
import { useStacklineTheme } from '@stackline/ui';
import { useAppSelector, useQueryParamValue } from 'src/utils/Hooks';
import { useBuildForecastInsightText } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/hooks';
import ForecastPlansKeyMetricCards from './Forecasts/ForecastPlansKeyMetricCards';
import ForecastsLineChart from './Forecasts/ForecastsLineChart';
import { CommonSummaryInfoSubtitle } from 'src/components/EntityPage/CommonSummaryInfo/CommonSummaryInfo';
import { CreateAdjustmentButton } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/CreateAdjustmentModal/CreateAdjustmentButton';
import ForecastChangeBarChart from './Forecasts/ForecastChangeBarChart';
import { getCardTitle } from 'src/components/BeaconV2Components/KeyMetricCards/SmartKeyMetricCard';
import useForecastMetricByWeekId from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useForecastMetricByWeekId';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import {
  BEACON_CHART_SPACING,
  BEACON_KEY_METRIC_CARD_SPACING,
  BEACON_GRID_TOP_SPACING
} from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import ForecastPlansAdjustmentsTable from './ForecastPlansAdjustmentsTable';
import ForecastAdjustmentsTypeProvider from 'src/components/BeaconV2Components/ExperimentalLayout/ForecastAdjustmentsTypeProvider';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { FORECAST_SUMMARY_KEYMETRIC_QUERY } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/ForecastQueryKeys';
import { useInvalidateQueriesOnInterval } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/hooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';

const titleOverrides = {
  'ratings-reviews-metrics': {
    weightedRating: 'Ratings & Reviews'
  },
  advertising: {
    clicks: 'Paid Traffic'
  },
  buybox: {
    winPercentage: 'Buy Box'
  }
};

export default memo(function ForecastsPlansPage() {
  const theme = useStacklineTheme();
  const entity = useAppSelector((state) => state.entityService.mainEntity);

  const selectedField = useQueryParamValue('keyMetric', 'organicClicks');
  const selectedIndex = useQueryParamValue('indexName', 'traffic');

  const { percentChange, formattedPrimaryTotal } = useForecastMetricByWeekId({
    indexName: selectedIndex,
    fieldName: selectedField
  });

  const splineChartInsight = useBuildForecastInsightText({
    changePercent: percentChange,
    fieldName: selectedField,
    fieldTitle: getCardTitle(selectedIndex, selectedField, titleOverrides),
    formattedTotal: formattedPrimaryTotal
  });

  useInvalidateQueriesOnInterval({ queryKeys: [FORECAST_SUMMARY_KEYMETRIC_QUERY], interval: 2 * 60 * 1000 });

  return (
    <>
      <ForecastAdjustmentsTypeProvider>
        <BasePageContainer>
          <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
            <Flex flexDirection="column" gap={BEACON_KEY_METRIC_CARD_SPACING}>
              <Flex flexDirection="column" gap="lg">
                <Flex flexDirection="column" gap={8}>
                  <Flex justifyContent="space-between">
                    <CommonSummaryInfoSubtitle title="Business Plans" />
                    <CreateAdjustmentButton />
                  </Flex>
                  <Text>
                    {`The metrics below summarize the business plans that impact the sales forecast for ${
                      ['client', 'brand'].includes(entity.type) ? entity.name : `this ${entity.type}`
                    }.`}
                  </Text>
                </Flex>
                <ForecastPlansKeyMetricCards titleOverrides={titleOverrides} />
              </Flex>
              <ForecastsLineChart
                fieldName={selectedField}
                indexName={selectedIndex}
                subtitle={splineChartInsight}
                titleOverrides={titleOverrides}
              />
            </Flex>
            <ForecastChangeBarChart fieldName={selectedField} showInsight />
          </Flex>
          <div
            style={{
              marginBottom: theme.spacing.xxl,
              marginTop: `${BEACON_GRID_TOP_SPACING}px`
            }}
          >
            <ForecastPlansAdjustmentsTable />
          </div>
        </BasePageContainer>
      </ForecastAdjustmentsTypeProvider>
    </>
  );
});
